"use client";

import posthog from "posthog-js";
import { useCallback, useMemo } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";

type OverlayVariant = "center" | "bottom";
type DismissMethod = "close_button" | "esc" | "backdrop" | "native_close";
type ShowTrigger = "scroll" | "dev_force";
type UpsellSurface = "overlay" | "inline";

interface BaseEventProps {
  variant: OverlayVariant;
  edition: ReaderReportType;
  story_slug: string | null;
  is_returning_dismisser: boolean;
}

type EmailWallEvent =
  | {
      name: "email_wall_shown";
      props: { trigger: ShowTrigger; scroll_progress: number };
    }
  | {
      name: "email_wall_dismissed";
      props: { method: DismissMethod; time_since_shown_ms: number | null };
    }
  | { name: "email_wall_submit_attempted"; props: Record<string, never> }
  | {
      name: "email_wall_submit_failed";
      props: { error_message: string | undefined };
    }
  | {
      name: "email_wall_submit_succeeded";
      props: {
        already_subscribed: boolean;
        time_since_shown_ms: number | null;
      };
    }
  | {
      name: "email_wall_upsell_offered";
      props: { other_edition: ReaderReportType; surface: UpsellSurface };
    }
  | {
      name: "email_wall_upsell_accepted";
      props: {
        other_edition: ReaderReportType;
        surface: UpsellSurface;
        time_since_offered_ms: number | null;
      };
    }
  | {
      name: "email_wall_upsell_skipped";
      props: { other_edition: ReaderReportType; surface: UpsellSurface };
    }
  | { name: "email_wall_inline_viewed"; props: Record<string, never> };

function deriveStorySlug(pathname: string | null): string | null {
  if (!pathname) {
    return null;
  }
  const segments = pathname.split("/").filter(Boolean);
  const storiesIndex = segments.indexOf("stories");
  if (storiesIndex === -1) {
    return null;
  }
  return segments[storiesIndex + 1] ?? null;
}

interface EmailWallAnalyticsParams {
  variant: OverlayVariant;
  edition: ReaderReportType;
  pathname: string | null;
  isReturningDismisser: boolean;
}

export function useEmailWallAnalytics({
  variant,
  edition,
  pathname,
  isReturningDismisser,
}: EmailWallAnalyticsParams) {
  const baseProps = useMemo<BaseEventProps>(
    () => ({
      variant,
      edition,
      story_slug: deriveStorySlug(pathname),
      is_returning_dismisser: isReturningDismisser,
    }),
    [variant, edition, pathname, isReturningDismisser]
  );

  const capture = useCallback(
    <E extends EmailWallEvent>(event: E) => {
      if (typeof window === "undefined") {
        return;
      }
      posthog.capture(event.name, { ...baseProps, ...event.props });
    },
    [baseProps]
  );

  return useMemo(
    () => ({
      trackShown: (props: { trigger: ShowTrigger; scroll_progress: number }) =>
        capture({ name: "email_wall_shown", props }),
      trackDismissed: (props: {
        method: DismissMethod;
        time_since_shown_ms: number | null;
      }) => capture({ name: "email_wall_dismissed", props }),
      trackSubmitAttempted: () =>
        capture({ name: "email_wall_submit_attempted", props: {} }),
      trackSubmitFailed: (props: { error_message: string | undefined }) =>
        capture({ name: "email_wall_submit_failed", props }),
      trackSubmitSucceeded: (props: {
        already_subscribed: boolean;
        time_since_shown_ms: number | null;
      }) => capture({ name: "email_wall_submit_succeeded", props }),
      trackUpsellOffered: (props: {
        other_edition: ReaderReportType;
        surface: UpsellSurface;
      }) => capture({ name: "email_wall_upsell_offered", props }),
      trackUpsellAccepted: (props: {
        other_edition: ReaderReportType;
        surface: UpsellSurface;
        time_since_offered_ms: number | null;
      }) => capture({ name: "email_wall_upsell_accepted", props }),
      trackUpsellSkipped: (props: {
        other_edition: ReaderReportType;
        surface: UpsellSurface;
      }) => capture({ name: "email_wall_upsell_skipped", props }),
      trackInlineViewed: () =>
        capture({ name: "email_wall_inline_viewed", props: {} }),
    }),
    [capture]
  );
}
